import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Notebook, Eye, ArrowLeft, Bell, Lock, Loader2, Pencil, Check, Plus, X, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fileUrl } from "@/lib/api";
import { colorClass } from "@/lib/noteColors";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n/LanguageContext";
import { sanitizeHtml, markdownToHtml } from "@/lib/richtext";
import { BrandMark } from "@/components/Brand";
import RichTextEditor from "@/components/RichTextEditor";
import ImageAttacher from "@/components/ImageAttacher";
import DocumentAttacher from "@/components/DocumentAttacher";
import ImageLightbox from "@/components/ImageLightbox";
import { format } from "date-fns";
import { toast } from "sonner";

const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

export default function PublicShare() {
  const { token } = useParams();
  const { t } = useT();
  const [note, setNote] = useState(null);
  const [status, setStatus] = useState("loading");
  const [passphrase, setPassphrase] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    api.get(`/public/share/${token}`)
      .then((r) => {
        if (r.data?.locked) { setStatus("locked"); return; }
        setNote(r.data); setDraft(r.data); setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [token]);

  // Keep the friend's view fresh so they see the owner's latest before editing.
  useEffect(() => {
    if (status !== "ok" || editing) return undefined;
    const id = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = passphrase.trim()
          ? await api.post(`/public/share/${token}/unlock`, { passphrase: passphrase.trim() })
          : await api.get(`/public/share/${token}`);
        if (data && !data.locked) { setNote(data); setDraft(data); }
      } catch { /* keep showing the last copy */ }
    }, 15000);
    return () => clearInterval(id);
  }, [status, editing, token, passphrase]);

  const unlock = async () => {
    if (!passphrase.trim()) return;
    try {
      setUnlocking(true); setError("");
      const { data } = await api.post(`/public/share/${token}/unlock`, { passphrase: passphrase.trim() });
      setNote(data); setDraft(data); setStatus("ok");
    } catch (e) {
      if (e.response?.status === 401) setError(t("unlock.wrong"));
      else setError(t("publicView.notFound"));
    } finally { setUnlocking(false); }
  };

  const save = async () => {
    if (!draft) return;
    try {
      setSaving(true);
      const payload = {
        title: draft.title,
        content: draft.content,
        is_checklist: draft.is_checklist,
        checklist_items: (draft.checklist_items || []).filter((i) => (i.text || "").trim()),
        images: draft.images || [],
        attachments: draft.attachments || [],
        base: {
          title: note.title, content: note.content, is_checklist: note.is_checklist,
          checklist_items: note.checklist_items || [], images: note.images || [], attachments: note.attachments || [],
        },
        base_updated_at: note.updated_at,
      };
      if (passphrase.trim()) payload.passphrase = passphrase.trim();
      const { data } = await api.post(`/public/share/${token}/edit`, payload);
      setNote(data); setDraft(data); setEditing(false);
      toast.success(t("publicView.saved"));
    } catch (e) {
      toast.error(t("publicView.notFound"));
    } finally { setSaving(false); }
  };

  const updateItem = (id, patch) => {
    setDraft((d) => ({ ...d, checklist_items: (d.checklist_items || []).map((i) => (i.id === id ? { ...i, ...patch } : i)) }));
  };
  const addItem = () => {
    setDraft((d) => ({
      ...d,
      is_checklist: true,
      checklist_items: [...(d.checklist_items || []), { id: genId(), text: "", checked: false }],
    }));
  };
  const removeItem = (id) => {
    setDraft((d) => ({ ...d, checklist_items: (d.checklist_items || []).filter((i) => i.id !== id) }));
  };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 text-stone-400 animate-spin" /></div>;

  if (status === "notfound") return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-stone-100 to-stone-50 dark:from-stone-950 dark:to-stone-900">
      <div className="max-w-md w-full bg-background rounded-3xl border border-border shadow-xl p-8 text-center">
        <h1 className="font-display font-bold text-2xl mb-2">{t("publicView.notFound")}</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-5">{t("publicView.notFoundHelp")}</p>
        <Link to="/"><Button variant="outline" className="rounded-full">Mango Notes</Button></Link>
      </div>
    </div>
  );

  if (status === "locked") return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-amber-50 to-rose-50 dark:from-stone-950 dark:to-stone-900">
      <div className="max-w-sm w-full bg-background rounded-3xl border border-border shadow-xl p-8" data-testid="unlock-form">
        <div className="h-14 w-14 rounded-2xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mx-auto mb-4">
          <Lock className="h-7 w-7 text-amber-700 dark:text-amber-300" />
        </div>
        <h1 className="font-display font-bold text-xl mb-2 text-center">{t("unlock.title")}</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-5 text-center">{t("unlock.subtitle")}</p>
        <Input value={passphrase} onChange={(e) => { setPassphrase(e.target.value); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && unlock()}
          placeholder={t("unlock.placeholder")} type="password" autoFocus data-testid="unlock-input" />
        {error && <p className="text-xs text-rose-600 mt-2" data-testid="unlock-error">{error}</p>}
        <Button onClick={unlock} disabled={unlocking || !passphrase.trim()} className="w-full mt-4 rounded-full" data-testid="unlock-btn">
          {unlocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
          {t("unlock.unlock")}
        </Button>
      </div>
    </div>
  );

  const canEdit = note?.role === "editor";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 h-14">
          <Link to="/" className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-800">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <BrandMark size="h-8 w-8" testId="public-logo" />
            <span className="font-display font-bold">{note.shared_from || "Mango Notes"}</span>
          </div>
          <span className={cn(
            "ml-auto inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1",
            canEdit
              ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
              : "bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300"
          )}>
            {canEdit ? <Pencil className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            {canEdit ? t("publicView.collaborator") : t("publicView.readOnly")}
          </span>
          {canEdit && !editing && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(true)} data-testid="public-edit-btn">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}
          {canEdit && editing && (
            <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid="public-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
              {saving ? t("publicView.saving") : t("publicView.saved")}
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="public-share-view">
        <article className={cn("rounded-3xl border p-6 sm:p-8 shadow-sm paper-grain", colorClass(note.color))}>
          {editing && canEdit ? (
            <>
              <ImageAttacher
                images={draft.images || []}
                onChange={(imgs) => setDraft({ ...draft, images: imgs })}
              />
              <div className="mt-3">
                <DocumentAttacher
                  attachments={draft.attachments || []}
                  onChange={(docs) => setDraft({ ...draft, attachments: docs })}
                />
              </div>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="border-0 shadow-none focus-visible:ring-0 px-0 text-3xl sm:text-4xl font-display font-bold bg-transparent mt-3"
                data-testid="public-edit-title" placeholder="Title" />
              {draft.is_checklist ? (
                <div className="space-y-1.5 pt-3" data-testid="public-edit-checklist">
                  {(draft.checklist_items || []).map((it, idx) => (
                    <div key={it.id} className="flex items-center gap-2">
                      <button
                        onClick={() => updateItem(it.id, { checked: !it.checked })}
                        className={cn(
                          "h-4 w-4 rounded border border-stone-400 dark:border-stone-500 flex items-center justify-center shrink-0",
                          it.checked && "bg-stone-900 border-stone-900 dark:bg-stone-100 dark:border-stone-100"
                        )}
                        aria-label="Toggle"
                      >
                        {it.checked && <span className="text-[10px] text-white dark:text-stone-900">✓</span>}
                      </button>
                      <input
                        value={it.text}
                        onChange={(e) => updateItem(it.id, { text: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                        autoFocus={idx === (draft.checklist_items || []).length - 1 && !it.text}
                        className={cn(
                          "flex-1 bg-transparent outline-none text-sm py-1",
                          it.checked && "line-through text-stone-400"
                        )}
                      />
                      <button
                        onClick={() => removeItem(it.id)}
                        className="h-6 w-6 inline-flex items-center justify-center rounded-full text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
                        aria-label="Remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pt-3">
                  <RichTextEditor
                    value={draft.content}
                    onChange={(html) => setDraft({ ...draft, content: html })}
                    placeholder="Take a note..."
                    testid="public-edit"
                    minHeight="260px"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {note.title && (
                <h1 className="font-display font-bold text-3xl sm:text-4xl mb-4 leading-tight" data-testid="public-title">{note.title}</h1>
              )}
              {note.images?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="public-images">
                  {note.images.map((p, i) => (
                    <img
                      key={p}
                      src={fileUrl(p)}
                      alt=""
                      className="w-full rounded-xl object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxIdx(i)}
                      data-testid={`public-image-${i}`}
                    />
                  ))}
                </div>
              )}
              {note.reminder && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 dark:bg-stone-800 px-3 py-1 text-xs font-medium text-stone-700 dark:text-stone-300 mb-4">
                  <Bell className="h-3 w-3" /> {safeDate(note.reminder)}
                </div>
              )}
              {note.is_checklist ? (
                <ul className="space-y-2" data-testid="public-checklist">
                  {note.checklist_items.map((it) => (
                    <li key={it.id} className="flex items-start gap-2.5">
                      <span className={cn("mt-1 h-4 w-4 rounded border border-stone-400 dark:border-stone-500 inline-flex items-center justify-center shrink-0",
                        it.checked && "bg-stone-900 border-stone-900 dark:bg-stone-100 dark:border-stone-100")}>
                        {it.checked && <span className="text-[9px] text-white dark:text-stone-900">✓</span>}
                      </span>
                      <span className={cn("break-words", it.checked && "line-through text-stone-400")}>{it.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                note.content && (
                  <div
                    className="richtext-view text-base leading-relaxed break-words"
                    data-testid="public-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(note.content)) }}
                  />
                )
              )}
              {note.attachments?.length > 0 && (
                <ul className="mt-5 space-y-1.5" data-testid="public-attachments">
                  {note.attachments.map((a) => {
                    const isAudio = (a.content_type || "").startsWith("audio/");
                    return (
                      <li
                        key={a.path}
                        className="flex items-center gap-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/50 px-2.5 py-2 text-sm"
                        data-testid={`public-attachment-${a.filename}`}
                      >
                        <FileText className="h-4 w-4 shrink-0 text-stone-500" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{a.filename}</div>
                          {isAudio && (
                            <audio controls src={fileUrl(a.path)} className="w-full max-w-sm mt-1" />
                          )}
                        </div>
                        <a
                          href={fileUrl(a.path)}
                          download={a.filename}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-7 w-7 inline-flex items-center justify-center rounded-full text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-200 dark:hover:bg-stone-700"
                          aria-label="Download"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </article>
        <p className="text-center text-xs text-stone-500 dark:text-stone-400 mt-6">
          <img src="/logo.png" alt="" className="inline h-4 w-4 mr-1 rounded align-[-3px]" />
          {t("publicView.sharedBy")} {note.shared_from || "Mango Notes"}
        </p>
      </main>

      <ImageLightbox
        images={note?.images || []}
        index={lightboxIdx}
        onIndexChange={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </div>
  );
}

function safeDate(iso) { try { return format(new Date(iso), "MMM d, yyyy"); } catch { return ""; } }
